import React, { useEffect, useState } from 'react';
import {
  MapPin, Building2, Navigation, Loader2, AlertCircle, Check,
  ArrowLeft, Stethoscope, Clock, Phone, Mail, ExternalLink, ChevronRight,
} from 'lucide-react';
import * as bookingsApi from '../api/bookings.api';
import { errorMessage } from '../api/client';

// ClinicPicker — geo-sorted list of clinics for the patient to choose from.
//
// Client 2026-09-09 revision — two-step flow so patients can COMPARE:
//   Step A. "List" mode: cards with clinic name / short address / distance.
//           Tapping a card opens the details view (no assignment yet).
//   Step B. "Details" mode: full clinic profile (address, doctor, hours,
//           phone, email, Google Maps link) + "Choose this clinic" primary
//           button + "Back to list" button. Only the primary button calls
//           the assign API.
//
// Behaviour:
//   • On mount → request navigator.geolocation.getCurrentPosition.
//   • With coords → GET /clinics?lat=X&lng=Y (server $geoNear-sorts).
//   • Without coords → GET /clinics (fallback ranking).
//   • On API failure while coords are set → retry without coords so the
//     patient always sees a pickable list.
//
// Props:
//   orderId  — MongoID of the order.
//   onPicked — (updatedOrder) => void, called after successful assignment.

function formatDistance(m) {
  const meters = Number(m);
  if (!Number.isFinite(meters) || meters < 0) return '';
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(meters < 10000 ? 1 : 0)} km`;
}

function ClinicPicker({ orderId, onPicked }) {
  const [coords, setCoords] = useState(null);
  const [gpsState, setGpsState] = useState('idle');       // idle | requesting | granted | denied | unavailable
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [selected, setSelected] = useState(null);         // clinic object in details view (null = list mode)
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsState('unavailable');
      return;
    }
    setGpsState('requesting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsState('granted');
      },
      (posErr) => {
        setGpsState(posErr && posErr.code === 1 ? 'denied' : 'unavailable');
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 },
    );
  }, []);

  useEffect(() => {
    if (gpsState === 'idle' || gpsState === 'requesting') return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const params = { limit: 50 };
        if (coords) { params.lat = coords.lat; params.lng = coords.lng; }
        let list;
        try {
          list = await bookingsApi.listPublicClinics(params);
        } catch (geoErr) {
          if (coords) {
            list = await bookingsApi.listPublicClinics({ limit: 50 });
          } else {
            throw geoErr;
          }
        }
        if (cancelled) return;
        setClinics(Array.isArray(list) ? list : []);
      } catch (e) {
        if (cancelled) return;
        setErr(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [gpsState, coords]);

  async function confirmChoice() {
    if (!selected || picking) return;
    setPicking(true);
    setErr('');
    try {
      const updated = await bookingsApi.assignClinic(orderId, selected._id);
      onPicked?.(updated);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setPicking(false);
    }
  }

  // ─── Details view ────────────────────────────────────────────────────
  if (selected) {
    const cp = selected.clinicProfile || {};
    const addr = [cp.addressLine1, cp.addressLine2, cp.city, cp.state, cp.postcode]
      .filter(Boolean).join(', ');
    const mapsUrl = addr
      ? `https://maps.google.com/?q=${encodeURIComponent([cp.clinicName || selected.name, addr].filter(Boolean).join(', '))}`
      : null;
    const distance = formatDistance(selected.distanceMeters);

    return (
      <div
        className="card"
        style={{
          marginTop: 20, padding: 20, textAlign: 'left',
          background: 'linear-gradient(180deg,#ecfeff 0%,#e0f2fe 100%)',
          border: '1px solid #7dd3fc',
        }}
      >
        <button
          type="button"
          onClick={() => { setSelected(null); setErr(''); }}
          disabled={picking}
          className="btn btn-ghost"
          style={{ marginBottom: 12, paddingLeft: 4 }}
        >
          <ArrowLeft size={14} /> Back to list
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <Building2 size={18} color="#0369a1" />
          <strong>Clinic details</strong>
        </div>

        <div style={{
          fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 8, marginBottom: 4,
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        }}>
          {cp.clinicName || selected.name || 'MyDr.AI clinic'}
          {distance && (
            <span style={{
              fontSize: 12, fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: 4,
              color: '#0369a1', background: '#e0f2fe', padding: '2px 8px', borderRadius: 999,
            }}>
              <Navigation size={11} /> {distance}
            </span>
          )}
        </div>

        <dl className="kvs" style={{ marginTop: 12, gridTemplateColumns: '1fr auto', gap: '6px 16px' }}>
          {addr && (
            <>
              <dt style={{ display: 'flex', alignItems: 'center', gap: 6 }}><MapPin size={14} /> Address</dt>
              <dd style={{ textAlign: 'right', maxWidth: 260 }}>{addr}</dd>
            </>
          )}
          {cp.prescribingDoctor && (
            <>
              <dt style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Stethoscope size={14} /> Doctor</dt>
              <dd style={{ textAlign: 'right' }}>{cp.prescribingDoctor}</dd>
            </>
          )}
          {cp.operatingHoursText && (
            <>
              <dt style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={14} /> Hours</dt>
              <dd style={{ textAlign: 'right', maxWidth: 260 }}>{cp.operatingHoursText}</dd>
            </>
          )}
          {selected.phone && (
            <>
              <dt style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Phone size={14} /> Phone</dt>
              <dd style={{ textAlign: 'right' }}><a href={`tel:${selected.phone}`}>{selected.phone}</a></dd>
            </>
          )}
          {selected.email && (
            <>
              <dt style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Mail size={14} /> Email</dt>
              <dd style={{ textAlign: 'right' }}><a href={`mailto:${selected.email}`}>{selected.email}</a></dd>
            </>
          )}
        </dl>

        {mapsUrl && (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline btn-block"
            style={{ marginTop: 14, background: 'white' }}
          >
            <ExternalLink size={14} /> Open in Google Maps
          </a>
        )}

        {err && <div className="notice warning" style={{ marginTop: 12 }}>{err}</div>}

        <button
          type="button"
          onClick={confirmChoice}
          disabled={picking}
          className="btn btn-primary btn-block"
          style={{ marginTop: 14 }}
        >
          {picking ? <span className="spinner" /> : <Check size={16} />}
          {picking ? 'Assigning…' : 'Choose this clinic'}
        </button>
      </div>
    );
  }

  // ─── List view ───────────────────────────────────────────────────────
  return (
    <div
      className="card"
      style={{
        marginTop: 20, padding: 20, textAlign: 'left',
        background: 'linear-gradient(180deg,#ecfeff 0%,#e0f2fe 100%)',
        border: '1px solid #7dd3fc',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Building2 size={18} color="#0369a1" />
        <strong>Choose your clinic</strong>
      </div>
      <p className="muted text-sm" style={{ marginTop: 4, marginBottom: 12 }}>
        {coords
          ? 'Clinics sorted by distance from your current location. Tap a clinic to view details before choosing.'
          : gpsState === 'requesting'
            ? 'Finding you on the map…'
            : 'Tap a clinic to view details before choosing.'}
      </p>

      {gpsState === 'requesting' && (
        <div className="row" style={{ alignItems: 'center', gap: 8, color: '#0369a1' }}>
          <Loader2 size={16} style={{ animation: 'spin .8s linear infinite' }} /> Getting your location…
        </div>
      )}

      {gpsState === 'denied' && (
        <div
          className="notice"
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            background: '#fff7ed', border: '1px solid #fed7aa', color: '#7c2d12',
            padding: 10, borderRadius: 8, marginBottom: 12,
          }}
        >
          <AlertCircle size={14} style={{ marginTop: 2 }} />
          <span>
            Location access was declined. You can still pick a clinic below —
            enable location in your browser and refresh to sort by distance.
          </span>
        </div>
      )}

      {loading && (
        <div className="row" style={{ alignItems: 'center', gap: 8, marginTop: 10, color: '#0369a1' }}>
          <Loader2 size={16} style={{ animation: 'spin .8s linear infinite' }} /> Loading clinics…
        </div>
      )}

      {err && <div className="notice warning" style={{ marginTop: 10 }}>{err}</div>}

      {!loading && !err && clinics.length === 0 && gpsState !== 'requesting' && (
        <div className="muted text-sm" style={{ marginTop: 10 }}>
          No clinics available right now. Please contact support.
        </div>
      )}

      {!loading && clinics.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0 0', display: 'grid', gap: 8 }}>
          {clinics.map((c) => {
            const cp = c.clinicProfile || {};
            const addr = [cp.addressLine1, cp.city, cp.state].filter(Boolean).join(', ');
            const distance = formatDistance(c.distanceMeters);
            return (
              <li key={c._id}>
                <button
                  type="button"
                  onClick={() => setSelected(c)}
                  style={{
                    width: '100%', textAlign: 'left',
                    background: 'white', border: '1px solid #cbd5e1', borderRadius: 10,
                    padding: 12, cursor: 'pointer',
                    display: 'grid', gap: 4,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontWeight: 700, color: '#0f172a' }}>
                      {cp.clinicName || c.name || 'MyDr.AI clinic'}
                    </div>
                    {distance && (
                      <div className="text-xs" style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        color: '#0369a1', background: '#e0f2fe', padding: '2px 8px', borderRadius: 999,
                      }}>
                        <Navigation size={11} /> {distance}
                      </div>
                    )}
                  </div>
                  {addr && (
                    <div className="text-xs muted" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <MapPin size={11} /> {addr}
                    </div>
                  )}
                  <div className="text-xs" style={{
                    color: '#0369a1', display: 'flex', alignItems: 'center',
                    gap: 4, marginTop: 2, fontWeight: 600,
                  }}>
                    View details <ChevronRight size={12} />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default ClinicPicker;
